import { Injectable, computed, inject, signal, effect } from '@angular/core';
import { GlobalVar } from '../../interfaces/global-var.interface';
import {
  EDIT_GLOBAL_VAR,
  GLOBAL_VAR_LIST,
  RELOAD_GLOBAL_VAR,
} from '../../constants/general.constants';
import { GeneralHelpers } from '../../helpers/general.helper';
import { StepDataHelper, type ConstantEntry } from '../../helpers/step-data.helper';
import { WorkflowService } from '../workflow/workflow.service';
import { MessageService } from '../message/message.service';
import { Subscription } from 'rxjs';
import _ from 'lodash';
import { Chunk } from '../../interfaces/step/step.interface';
import { LocalforageService } from '../localforage/localforage.service';
import { Notebook } from '../../interfaces/project.interface';
import { AppStateService } from '../app-state/app-state.service';
import { ProjectService } from '../project/project.service';
import { PermissionId } from '../../constants/permissions.constants';

@Injectable({
  providedIn: 'root',
})
export class NotebookVariablesService {
  private notebookService = inject(WorkflowService);
  private messageService = inject(MessageService);
  private localForageService = inject(LocalforageService);

  #appState = inject(AppStateService);
  #projectService = inject(ProjectService);

  // Backward compatibility - keep original array
  public variableList: GlobalVar[] | any = [];

  // Signal-based reactive list and count for UI reactivity
  readonly #variableListSignal = signal<GlobalVar[]>([]);
  readonly variableList$ = this.#variableListSignal.asReadonly();
  variableListCount = computed(() => this.#variableListSignal().length);

  // Helper method to update both array and signal
  private updateVariableList(value: GlobalVar[] | any): void {
    if (Array.isArray(value)) {
      this.variableList = value;
      this.#variableListSignal.set(value);
    } else {
      this.variableList = [];
      this.#variableListSignal.set([]);
    }
  }

  /**
   * Replace variables with snapshot content and persist as cache overrides
   */
  public setVariablesFromSnapshot(variables: GlobalVar[] | unknown): void {
    const normalized = Array.isArray(variables)
      ? variables.map((variable) => {
        const existingSource = (variable as GlobalVar)?.source;
        if (existingSource === 'default' || existingSource === 'cache') {
          return { ...variable, source: existingSource };
        }

        const willSaveToServer = this.#appState.isHeadVersion() &&
          this.hasWritePermission() &&
          this.#appState.isCurrentEditor();
        return { ...variable, source: willSaveToServer ? 'default' : 'cache' };
      })
      : [];

    this.updateVariableList(normalized);
    this.setNotebookVariablesToLocalStorage(normalized);
  }

  public globalVarsShown: boolean = true;
  public firstTime: boolean = true;
  private hasWritePermission = computed(() =>
    this.#projectService.getCurrentUserHasProjectPermission(PermissionId.WRITE)
  );
  private messageSubscription!: Subscription;

  private previousVersionId: number = 0;
  private previousWorkflowId: number = 0;

  constructor() {
    // Note: Variables are loaded via the effect below once appState is ready
    // This ensures workflowId, versionId, and userId are all available before loading

    this.messageSubscription = this.messageService
      .getMessage()
      .subscribe((message: any) => {
        if (message && message.text === RELOAD_GLOBAL_VAR) {
          this.clean();
          this.getNotebookVariablesFromServer();
        }
      });

    // Watch for version/workflow changes AND initial load to reload variables
    // This ensures variables are reloaded when switching between versions
    // AND properly loads localStorage when appState is ready on initial page load
    effect(() => {
      const versionId = this.#appState.versionId();
      const workflowId = this.#appState.workflowId();
      const userId = this.#appState.user()?.userId;

      // Only proceed if we have all required IDs (appState is ready)
      if (!workflowId || !versionId || !userId) {
        return;
      }

      const isVersionChange = versionId !== this.previousVersionId;
      const isWorkflowChange = workflowId !== this.previousWorkflowId;
      const isInitialLoad = this.previousVersionId === 0 && this.previousWorkflowId === 0;

      if (isVersionChange || isWorkflowChange || isInitialLoad) {
        console.log('NotebookVariablesService: Loading variables', {
          isInitialLoad,
          isVersionChange,
          isWorkflowChange,
          previousVersionId: this.previousVersionId,
          newVersionId: versionId,
          previousWorkflowId: this.previousWorkflowId,
          newWorkflowId: workflowId,
          userId
        });

        // For version/workflow changes AND initial load, reload from server first (gets defaults for version)
        // The server call will automatically load localStorage overrides after server defaults are loaded
        this.getNotebookVariablesFromServer();

        this.previousVersionId = versionId;
        this.previousWorkflowId = workflowId;
      }
    });
  }

  // Add
  public add(globalVar: GlobalVar): GlobalVar[] {
    const existingIndex = this.getGlobalVarIndex(globalVar.id);
    if (existingIndex > -1) {
      this.update(globalVar.id, globalVar);
    } else {
      // Ensure the variable has a valid unique ID
      if (!globalVar.id) {
        globalVar.id = GeneralHelpers.getUuid();
      }
      
      // Determine source based on where it will be saved
      // If saving to server (HEAD version + locked), mark as 'default'
      // Otherwise, mark as 'cache' (localStorage)
      const willSaveToServer = this.#appState.isHeadVersion() && 
                               this.hasWritePermission() && 
                               this.#appState.isCurrentEditor();
      globalVar.source = willSaveToServer ? 'default' : 'cache';
      
      this.variableList.push(globalVar);
      this.#variableListSignal.set([...this.variableList]); // Sync signal
      this.saveVariables();
    }
    return this.variableList;
  }

  // Update
  public update(id: string, data: GlobalVar): GlobalVar[] {
    const index = this.getGlobalVarIndex(id);
    if (index > -1) {
      Object.assign(this.variableList[index], data);
      
      // Determine source based on where it will be saved
      // If saving to server (HEAD version + locked), mark as 'default'
      // Otherwise, mark as 'cache' (localStorage override)
      const willSaveToServer = this.#appState.isHeadVersion() && 
                               this.hasWritePermission() && 
                               this.#appState.isCurrentEditor();
      this.variableList[index].source = willSaveToServer ? 'default' : 'cache';
      
      this.#variableListSignal.set([...this.variableList]); // Sync signal
      this.saveVariables();
    }
    return this.variableList;
  }

  // Delete
  public delete(id: string): GlobalVar[] {
    const index = this.getGlobalVarIndex(id);
    if (index > -1) {
      this.variableList.splice(index, 1);
      this.#variableListSignal.set([...this.variableList]); // Sync signal
      this.saveVariables();
    }
    return this.variableList;
  }

  // Edit
  public edit(id: string) {
    const index = this.getGlobalVarIndex(id);
    if (index > -1) {
      this.messageService.sendMessage(EDIT_GLOBAL_VAR, {
        variable: this.variableList[index],
        index: index,
      });
    }
  }

  /**
   * Write a step's `data` back as workflow variables.
   *
   * `constants` must be the project constants the step's `data` was built with
   * (StepDataHelper.buildStepDataMap), so the ones it merely carried through are
   * not promoted to workflow variables. See StepDataHelper.withoutUnchangedConstants.
   */
  public addVarsFromRawToList(
    rawData: any,
    chunkData: Chunk,
    constants: ConstantEntry[] | null | undefined,
  ): void {
    const data =
      rawData && typeof rawData === 'object'
        ? StepDataHelper.withoutUnchangedConstants(rawData, constants, this.variableList)
        : rawData;
    if (GeneralHelpers.detectEmptyObject(data)) {
      return;
    }

    let hasChanges = false;

    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const value = data[key];
        const type = GeneralHelpers.trueTypeOf(value);

        if (
          ['number', 'string', 'boolean', 'null', 'undefined'].every(
            (typeNotation) => typeNotation !== type
          ) &&
          !!data[key] === false
        ) {
          return;
        }

        const existingIndex = GeneralHelpers.findIndexInArrayByProp(
          this.variableList,
          'name',
          key
        );

        if (existingIndex > -1) {
          const existingVariable = this.variableList[existingIndex] as GlobalVar;
          const hasValueChanged = !_.isEqual(existingVariable.value, data[key]);
          const source: 'default' | 'cache' =
            existingVariable.source === 'cache' || hasValueChanged ? 'cache' : 'default';

          Object.assign(this.variableList[existingIndex], {
            name: key,
            value: data[key],
            id: this.variableList[existingIndex].id,
            sortOrder: this.variableList[existingIndex].sortOrder,
            stepId: this.variableList[existingIndex].stepId,
            disabled: false,
            source,
          });
          hasChanges = true;
        } else {
          this.variableList.push({
            name: key,
            value: data[key],
            id: GeneralHelpers.getUuid(),
            sortOrder: chunkData.sortOrder,
            stepId: chunkData.stepId,
            disabled: false,
            source: 'cache',
          } as GlobalVar);
          hasChanges = true;
        }
      }
    }

    if (hasChanges) {
      this.#variableListSignal.set([...this.variableList]); // Sync signal
    }

    this.saveVariables();
  }

  public syncWithChunkList(chunks: Chunk[]) {
    if (this.variableList?.length > 0) {
      let hasChanges = false;

      for (let index = 0; index < this.variableList.length; index++) {
        const element: GlobalVar = this.variableList[index];
        const chunk = GeneralHelpers.findElementInArrayByProp(
          chunks as any,
          'stepId',
          element.stepId
        );

        if (chunk !== undefined) {
          element.sortOrder = Number(chunk['sortOrder']);
          hasChanges = true;
        }
      }

      if (hasChanges) {
        this.#variableListSignal.set([...this.variableList]); // Sync signal
      }

      this.saveVariables();
    }
  }

  // Get Global Var Index
  private getGlobalVarIndex(id: string): number {
    return this.variableList.findIndex((item: GlobalVar) => item.id === id);
  }

  public getGlobalVarByName(name: string): GlobalVar | null {
    const element = this.variableList.find(
      (globalVar: GlobalVar) => globalVar.name === name
    );
    return element || null;
  }

  public getGlobalVarValueDeep(keyString: string): any {
    const dot = '.';
    const dataKey = 'data';
    const splittedKeys = keyString.split(dot);
    if (splittedKeys?.length > 0) {
      const firstElement = splittedKeys.shift();
      if (firstElement !== dataKey && firstElement !== undefined) {
        splittedKeys.unshift(firstElement);
      }
    }
    const globalVarName = splittedKeys[0];
    const globalVar = this.getGlobalVarByName(globalVarName);
    if (splittedKeys?.length > 1) {
      splittedKeys.shift();
      return _.get(globalVar?.value, splittedKeys.join(dot));
    }
    return globalVar ? globalVar.value : globalVar;
  }

  public saveVariables() {
    // Only save to server if:
    // 1. User is on head version
    // 2. User has write permission
    // 3. Project is locked by current user (required for PUT requests)
    if (this.#appState.isHeadVersion() && this.hasWritePermission() && this.#appState.isCurrentEditor()) {
      this.setNotebookVariablesToServer();
    } else {
      // Fall back to localStorage if project is not locked or user doesn't have permission
      this.setNotebookVariablesToLocalStorage();
    }
  }

  public setNotebookVariablesToLocalStorage(
    variableList: GlobalVar[] | any = this.variableList,
    workflowId: string | any = this.#appState.workflowId().toString()
  ) {
    if (!workflowId || workflowId.includes('Object')) {
      console.warn('⚠️ Cannot save variables to localStorage: invalid workflowId', workflowId);
      return;
    }

    // Include versionId and userId in storage key for per-user-per-version persistence
    const versionId = this.#appState.versionId();
    const userId = this.#appState.user()?.userId;

    if (!versionId || versionId === 0) {
      console.warn('⚠️ Cannot save variables to localStorage: invalid versionId', versionId);
      return;
    }

    if (!userId) {
      console.warn('⚠️ Cannot save variables to localStorage: user not authenticated');
      return;
    }

    // CRITICAL: Only save cache variables to localStorage, not defaults
    // This prevents overwriting user's cache with server defaults
    const cacheVariables = Array.isArray(variableList) 
      ? variableList.filter((v: GlobalVar) => v.source === 'cache' || !v.source)
      : variableList;

    const storageKey = `${GLOBAL_VAR_LIST}_${workflowId}_${versionId}_${userId}`;

    this.localForageService.setItem(
      storageKey,
      GeneralHelpers.jsonStringify(cacheVariables)
    );

    console.log(`💾 Saved ${cacheVariables?.length || 0} cache variables to localStorage for version ${versionId}, user ${userId} (filtered from ${variableList?.length || 0} total)`);
  }
  
  /**
   * Load variables from localStorage for current user, version, and workflow
   * This ensures variables persist across page reloads per user per version
   * 
   * NOTE: This method is kept for backward compatibility but is now handled
   * by loadAndMergeVariables() which properly merges cache with defaults.
   * Direct calls to this method should be avoided.
   */
  public getNotebookVariablesFromLocalStorage(): void {
    console.warn('⚠️ getNotebookVariablesFromLocalStorage() called directly - this should be handled by loadAndMergeVariables()');
    
    const workflowId = this.#appState.workflowId();
    const versionId = this.#appState.versionId();
    const userId = this.#appState.user()?.userId;

    // Validate all required IDs are present
    if (!workflowId || workflowId === 0) {
      console.warn('⚠️ Cannot load variables from localStorage: invalid workflowId', workflowId);
      return;
    }

    if (!versionId || versionId === 0) {
      console.warn('⚠️ Cannot load variables from localStorage: invalid versionId', versionId);
      return;
    }

    if (!userId) {
      console.warn('⚠️ Cannot load variables from localStorage: user not authenticated');
      return;
    }

    const storageKey = `${GLOBAL_VAR_LIST}_${workflowId}_${versionId}_${userId}`;

    this.localForageService.getItem(storageKey).then((stored: unknown) => {
      if (stored && typeof stored === 'string') {
        try {
          const parsed = GeneralHelpers.jsonParse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            // Mark as cache variables
            parsed.forEach((v: GlobalVar) => v.source = 'cache');
            this.updateVariableList(parsed);
            console.log(`📦 Loaded ${parsed.length} cache variables from localStorage for version ${versionId}, user ${userId}`);
          }
        } catch (error) {
          console.error('❌ Error parsing variables from localStorage:', error);
        }
      }
    }).catch((error) => {
      console.error('❌ Error loading variables from localStorage:', error);
    });
  }

  private setNotebookVariablesToServer(
    variableList: GlobalVar[] | any = this.variableList,
    workflowId: string | any = this.#appState.workflowId().toString()
  ) {
    // Double-check that project is locked by current user before making PUT request
    // This prevents unnecessary requests when project is not locked
    if (!this.#appState.isCurrentEditor()) {
      console.warn('Project is not locked by current user - saving to localStorage instead');
      this.setNotebookVariablesToLocalStorage(variableList, workflowId);
      return;
    }

    // A `job_arguments` variable is never written to the server defaultState.
    // Editors before EHQ-388 cached run inputs under that name (source 'cache');
    // a stale copy restored from localStorage must not be persisted.
    const persisted = (Array.isArray(variableList) ? variableList : []).filter(
      (v: GlobalVar) => !this.isJobArgumentsName(v.name),
    );

    this.notebookService
      .update(
        {
          defaultState: JSON.stringify(persisted),
        },
        workflowId,
        this.#appState.projectId().toString()
      )
      .subscribe({
        next: (data: Notebook | any) => {
          this.setNotebookVariablesToLocalStorage(data);
        },
        error: (error) => {
          // Handle "project is not locked" error gracefully
          const errorMessage = error?.error?.message || '';
          if (errorMessage.includes('project is not locked') || errorMessage.includes('not locked')) {
            console.warn('Project is not locked - saving to localStorage instead:', errorMessage);
            // Fall back to localStorage when project is not locked
            this.setNotebookVariablesToLocalStorage(variableList, workflowId);
          } else {
            console.error('setNotebookVariablesToServer: ', error);
            // For other errors, still save to localStorage as fallback
            this.setNotebookVariablesToLocalStorage(variableList, workflowId);
          }
        },
      });
  }

  private getNotebookVariablesFromServer(
    workflowId: string | undefined = this.#appState.workflowId().toString(),
    projectId: string | undefined = this.#appState.projectId().toString()
  ) {
    const hasBothParams = !!workflowId && !!projectId;
    const areZero = workflowId == '0' && projectId == '0';
    if (!hasBothParams || areZero) {
      return;
    }

    // Guard against transient state during project navigation: projectId,
    // versionId, and workflowId are read from separate signals that update
    // sequentially. If the (project, version, workflow) triple isn't
    // internally consistent (active version belongs to a different project,
    // or active workflow belongs to a different version), the API URL would
    // address a non-existent resource and return 404 "Resource Not Found".
    // The reactive effect re-fires once state settles (SCRUM-674).
    const activeVersion = this.#appState.version();
    if (activeVersion && activeVersion.projectId !== this.#appState.projectId()) {
      return;
    }
    const activeWorkflow = this.#appState.workflow();
    if (activeWorkflow && activeWorkflow.versionId !== this.#appState.versionId()) {
      return;
    }

    this.notebookService.getById(workflowId, projectId).subscribe({
      next: (data: Notebook | any) => {
        // Load default variables from server (version defaults)
        let defaultVariables: GlobalVar[] = [];
        
        if (data?.defaultState && GeneralHelpers.isJSON(data.defaultState)) {
          const parsed = GeneralHelpers.jsonParse(data.defaultState);
          const rawVars = Array.isArray(parsed) ? parsed : [];
          // Map {key, value, dataType} from defaultState to GlobalVar format
          defaultVariables = rawVars.map((v: any) => ({
            name: v.key ?? v.name ?? '',
            value: v.value ?? '',
            id: v.id ?? GeneralHelpers.getUuid(),
            sortOrder: v.sortOrder ?? 0,
            stepId: v.stepId ?? 0,
            disabled: v.disabled ?? false,
            source: 'default' as const,
          }));
        }
        
        // Now load cache variables and merge with defaults
        this.loadAndMergeVariables(defaultVariables, workflowId);
      },
      error: (error) => {
        console.error('getNotebookVariablesFromServer: ', error);
        // On error, try loading from localStorage with empty defaults
        this.loadAndMergeVariables([], workflowId);
      },
    });
  }

  /**
   * Load cache variables from localStorage and merge with defaults
   * Cache variables override defaults when they have the same name
   */
  private loadAndMergeVariables(defaultVariables: GlobalVar[], workflowId: string): void {
    const versionId = this.#appState.versionId();
    const userId = this.#appState.user()?.userId;

    if (!versionId || versionId === 0 || !userId) {
      // No valid version/user, just use defaults
      this.updateVariableList(defaultVariables);
      console.log(`📋 Loaded ${defaultVariables.length} default variables (no cache available)`);
      return;
    }

    const storageKey = `${GLOBAL_VAR_LIST}_${workflowId}_${versionId}_${userId}`;

    this.localForageService.getItem(storageKey).then((stored: unknown) => {
      let cacheVariables: GlobalVar[] = [];
      
      if (stored && typeof stored === 'string') {
        try {
          const parsed = GeneralHelpers.jsonParse(stored);
          cacheVariables = Array.isArray(parsed) ? parsed : [];
          // Mark all cache variables as 'cache' source only when missing
          cacheVariables.forEach(v => {
            if (!v.source) {
              v.source = 'cache';
            }
          });
        } catch (error) {
          console.error('❌ Error parsing cache variables:', error);
          cacheVariables = [];
        }
      }

      // Merge: cache overrides defaults
      const mergedVariables = this.mergeVariables(defaultVariables, cacheVariables);
      this.updateVariableList(mergedVariables);
      
      console.log(`📋 Loaded variables for version ${versionId}, user ${userId}:`, {
        defaults: defaultVariables.length,
        cache: cacheVariables.length,
        merged: mergedVariables.length,
        cacheOverrides: cacheVariables.filter(cv => 
          defaultVariables.some(dv => dv.name === cv.name)
        ).length
      });
    }).catch((error) => {
      console.error('❌ Error loading cache variables:', error);
      // Fall back to defaults only
      this.updateVariableList(defaultVariables);
    });
  }

  /**
   * Merge default and cache variables
   * Cache variables override defaults when names match
   * @param defaults - Variables from server (workflow.defaultState)
   * @param cache - Variables from localStorage (user-specific, version-specific)
   * @returns Merged variable list with cache taking precedence
   */
  private mergeVariables(defaults: GlobalVar[], cache: GlobalVar[]): GlobalVar[] {
    // Start with a copy of defaults
    const merged = [...defaults];
    
    // Override defaults with cache values (by name)
    cache.forEach(cacheVar => {
      const defaultIndex = merged.findIndex(v => v.name === cacheVar.name);
      
      if (defaultIndex >= 0) {
        // Override default with cache value
        const overrideVar = { ...cacheVar };
        overrideVar.source = 'cache';
        merged[defaultIndex] = overrideVar;
      } else {
        // Add new cache-only variable
        const newVar = { ...cacheVar };
        newVar.source = 'cache';
        merged.push(newVar);
      }
    });
    
    return merged;
  }

  /**
   * Reset a variable to its default value by removing the cache override
   * @param variableName - Name of the variable to reset
   */
  public resetToDefault(variableName: string): void {
    const variable = this.getGlobalVarByName(variableName);
    
    if (!variable) {
      console.warn(`⚠️ Variable "${variableName}" not found`);
      return;
    }
    
    if (variable.source !== 'cache') {
      console.warn(`⚠️ Variable "${variableName}" is already at default value`);
      return;
    }
    
    // Remove from current list
    const index = this.getGlobalVarIndex(variable.id);
    if (index > -1) {
      this.variableList.splice(index, 1);
      this.#variableListSignal.set([...this.variableList]);
    }
    
    // Save to localStorage (this will remove the cache override)
    this.saveVariables();
    
    // Reload from server to get the default value back
    this.getNotebookVariablesFromServer();
    
    console.log(`🔄 Reset variable "${variableName}" to default value`);
  }

  /**
   * Clear all cache variables and restore defaults
   * This is equivalent to applying the default snapshot
   */
  public clearAllCacheVariables(): void {
    const versionId = this.#appState.versionId();
    const userId = this.#appState.user()?.userId;
    const workflowId = this.#appState.workflowId();
    
    if (!versionId || !userId || !workflowId) {
      console.warn('⚠️ Cannot clear cache: invalid context');
      return;
    }
    
    const storageKey = `${GLOBAL_VAR_LIST}_${workflowId}_${versionId}_${userId}`;
    
    // Remove from localStorage
    this.localForageService.removeItem(storageKey).then(() => {
      console.log(`🗑️ Cleared all cache variables for version ${versionId}, user ${userId}`);
      
      // Reload from server to get defaults
      this.getNotebookVariablesFromServer();
    }).catch((error) => {
      console.error('❌ Error clearing cache variables:', error);
    });
  }

  public clean() {
    this.updateVariableList([]); // Clear both array and signal
  }

  /**
   * Canonical match for the legacy `job_arguments` variable name. Mirrors
   * SimpleUIService.isJobArgumentsVariableName; defined here (not imported) to
   * avoid an import cycle (SimpleUIService injects this service).
   */
  private isJobArgumentsName(name?: string): boolean {
    const normalized = (name ?? '').trim().toLowerCase();
    return normalized === 'job_arguments' || normalized === 'jobarguments';
  }

  /**
   * Operator-supplied variable_schema values for editor runs, keyed by
   * workflow id (EHQ-388). getJobArguments() reads these directly, the way
   * enoch reads `exec_job.arguments`; nothing is written to the variable list,
   * localStorage or the server defaultState.
   *
   * Kept per workflow for the page's lifetime, so a solo step run after a
   * "Run All Steps" still sees the inputs that run used.
   */
  readonly #runJobArguments = new Map<string, Record<string, unknown>>();

  /** Set the run inputs for the current workflow (replaces any earlier ones). */
  public setRunJobArguments(values: Record<string, unknown>): void {
    this.#runJobArguments.set(this.#currentWorkflowKey(), { ...(values ?? {}) });
  }

  /** Forget the current workflow's run inputs (a run without a schema). */
  public clearRunJobArguments(): void {
    this.#runJobArguments.delete(this.#currentWorkflowKey());
  }

  /** The current workflow's run inputs, or null when none were supplied. */
  public getRunJobArguments(): Record<string, unknown> | null {
    return this.#runJobArguments.get(this.#currentWorkflowKey()) ?? null;
  }

  #currentWorkflowKey(): string {
    return String(this.#appState.workflowId() ?? '');
  }
}
